import { FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import { animated } from '@react-spring/web';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import PropTypes from 'prop-types';
import React, { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { ScreenElement, PlaceholderButton, Close } from '@micromag/core/components';
import { usePlaybackContext, usePlaybackMediaRef, useScreenState, useScreenSize, useViewerContext, useViewerWebView, useViewerInteraction, useScreenRenderContext } from '@micromag/core/contexts';
import { useTrackScreenEvent, useDimensionObserver, useDragProgress } from '@micromag/core/hooks';
import { isHeaderFilled, isFooterFilled, getFooterProps, isTextFilled, camelCase, getStyleFromBox, getStyleFromAlignment } from '@micromag/core/utils';
import Background from '@micromag/element-background';
import Button from '@micromag/element-button';
import CallToAction from '@micromag/element-call-to-action';
import Container from '@micromag/element-container';
import Footer from '@micromag/element-footer';
import Header from '@micromag/element-header';
import Heading from '@micromag/element-heading';
import Keypad from '@micromag/element-keypad';
import Layout from '@micromag/element-layout';
import Scroll from '@micromag/element-scroll';
import Text from '@micromag/element-text';
import Visual from '@micromag/element-visual';

var styles = {"popupButton":"micromag-screen-keypad-popupButton","popup":"micromag-screen-keypad-popup","close":"micromag-screen-keypad-close","layoutLabelOver":"micromag-screen-keypad-layoutLabelOver","emptyButtonLabel":"micromag-screen-keypad-emptyButtonLabel","buttonLabel":"micromag-screen-keypad-buttonLabel","withSquareItems":"micromag-screen-keypad-withSquareItems","layoutNoLabel":"micromag-screen-keypad-layoutNoLabel","thumbnail":"micromag-screen-keypad-thumbnail","popupBackdrop":"micromag-screen-keypad-popupBackdrop","button":"micromag-screen-keypad-button","background":"micromag-screen-keypad-background","container":"micromag-screen-keypad-container","disabled":"micromag-screen-keypad-disabled","hidden":"micromag-screen-keypad-hidden","placeholder":"micromag-screen-keypad-placeholder","content":"micromag-screen-keypad-content","emptyTitle":"micromag-screen-keypad-emptyTitle","emptyText":"micromag-screen-keypad-emptyText","isPlaceholder":"micromag-screen-keypad-isPlaceholder","layout":"micromag-screen-keypad-layout","fixedHeader":"micromag-screen-keypad-fixedHeader","open":"micromag-screen-keypad-open","closeIcon":"micromag-screen-keypad-closeIcon","header":"micromag-screen-keypad-header","footer":"micromag-screen-keypad-footer","inner":"micromag-screen-keypad-inner","grid":"micromag-screen-keypad-grid","item":"micromag-screen-keypad-item","placeholderItem":"micromag-screen-keypad-placeholderItem","isEmpty":"micromag-screen-keypad-isEmpty","disableHover":"micromag-screen-keypad-disableHover","isLink":"micromag-screen-keypad-isLink","imagePlaceholder":"micromag-screen-keypad-imagePlaceholder","buttonPlaceholder":"micromag-screen-keypad-buttonPlaceholder","buttonVisualPlaceholder":"micromag-screen-keypad-buttonVisualPlaceholder","empty":"micromag-screen-keypad-empty","emptyButtonVisual":"micromag-screen-keypad-emptyButtonVisual","popupScroll":"micromag-screen-keypad-popupScroll","popupInner":"micromag-screen-keypad-popupInner","withShadow":"micromag-screen-keypad-withShadow","popupContent":"micromag-screen-keypad-popupContent","popupHeading":"micromag-screen-keypad-popupHeading","popupVisual":"micromag-screen-keypad-popupVisual","contentSplit":"micromag-screen-keypad-contentSplit","emptyHeading":"micromag-screen-keypad-emptyHeading","emptyVisual":"micromag-screen-keypad-emptyVisual","emptyContent":"micromag-screen-keypad-emptyContent","emptyCTA":"micromag-screen-keypad-emptyCTA","popupCTA":"micromag-screen-keypad-popupCTA","contentBottom":"micromag-screen-keypad-contentBottom","layoutLabelTop":"micromag-screen-keypad-layoutLabelTop","buttonVisual":"micromag-screen-keypad-buttonVisual","fillImage":"micromag-screen-keypad-fillImage"};

var placeholders = [{
  id: '1'
}, {
  id: '2'
}, {
  id: '3'
}, {
  id: '4'
}, {
  id: '5'
}, {
  id: '6'
}, {
  id: '7'
}, {
  id: '8'
}, {
  id: '9'
}];
var placeholderPopupBoxStyles = {
  padding: {
    left: 30,
    top: 30,
    right: 30,
    bottom: 30
  }
};
var stopDragEventsPropagation = {
  onTouchMove: function onTouchMove(e) {
    return e.stopPropagation();
  },
  onTouchStart: function onTouchStart(e) {
    return e.stopPropagation();
  },
  onTouchEnd: function onTouchEnd(e) {
    return e.stopPropagation();
  },
  onPointerMove: function onPointerMove(e) {
    return e.stopPropagation();
  },
  onPointerUp: function onPointerUp(e) {
    return e.stopPropagation();
  },
  onPointerDown: function onPointerDown(e) {
    return e.stopPropagation();
  }
};
var mouseBlocker = _objectSpread(_objectSpread({}, stopDragEventsPropagation), {}, {
  onClick: function onClick(e) {
    return e.stopPropagation();
  },
  style: {
    position: 'fixed',
    zIndex: '1000',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    cursor: 'default'
  }
});
var propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    heading: PropTypes$1.headingElement,
    description: PropTypes$1.textElement,
    visual: PropTypes$1.visualElement,
    boxStyle: PropTypes$1.boxStyle
  })),
  title: PropTypes$1.headingElement,
  subtitle: PropTypes$1.textElement,
  layout: PropTypes.oneOf(['top', 'middle', 'bottom']),
  spacing: PropTypes.number,
  keypadSettings: PropTypes.shape({
    layout: PropTypes.shape({
      columnAlign: PropTypes.oneOf(['left', 'right', 'middle']),
      columns: PropTypes.number,
      spacing: PropTypes.number,
      withSquareItems: PropTypes.bool
    })
  }),
  buttonStyles: PropTypes.shape({
    layout: PropTypes.string,
    textStyle: PropTypes$1.textStyle,
    boxStyle: PropTypes$1.boxStyle,
    fillImage: PropTypes.bool,
    alignment: PropTypes$1.alignment
  }),
  popupStyles: PropTypes.shape({
    layout: PropTypes.oneOf(['content-top', 'content-split', 'content-bottom']),
    headingTextStyle: PropTypes$1.textStyle,
    contentTextStyle: PropTypes$1.textStyle,
    boxStyle: PropTypes$1.boxStyle
  }),
  header: PropTypes$1.header,
  footer: PropTypes$1.footer,
  background: PropTypes$1.backgroundElement,
  current: PropTypes.bool,
  preload: PropTypes.bool,
  withoutCloseButton: PropTypes.bool,
  className: PropTypes.string
};
var defaultProps = {
  items: null,
  title: null,
  subtitle: null,
  layout: null,
  spacing: 20,
  keypadSettings: null,
  buttonStyles: null,
  popupStyles: null,
  header: null,
  footer: null,
  background: null,
  current: true,
  preload: true,
  withoutCloseButton: false,
  className: null
};
var KeypadScreen = function KeypadScreen(_ref) {
  var items = _ref.items,
    title = _ref.title,
    subtitle = _ref.subtitle,
    layout = _ref.layout,
    spacing = _ref.spacing,
    keypadSettings = _ref.keypadSettings,
    buttonStyles = _ref.buttonStyles,
    popupStyles = _ref.popupStyles,
    header = _ref.header,
    footer = _ref.footer,
    background = _ref.background,
    current = _ref.current,
    preload = _ref.preload,
    withoutCloseButton = _ref.withoutCloseButton,
    className = _ref.className;
  var containerRef = useRef(null);
  var popupInnerRef = useRef(null);
  var trackScreenEvent = useTrackScreenEvent('keypad');
  var _usePlaybackContext = usePlaybackContext(),
    muted = _usePlaybackContext.muted;
  var mediaRef = usePlaybackMediaRef(current);
  var screenState = useScreenState();
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    resolution = _useScreenSize.resolution;
  var _useViewerContext = useViewerContext(),
    viewerTopHeight = _useViewerContext.topHeight,
    viewerBottomHeight = _useViewerContext.bottomHeight,
    viewerBottomSidesWidth = _useViewerContext.bottomSidesWidth;
  var _useViewerWebView = useViewerWebView(),
    openWebView = _useViewerWebView.open;
  var _useViewerInteraction = useViewerInteraction(),
    enableInteraction = _useViewerInteraction.enableInteraction,
    disableInteraction = _useViewerInteraction.disableInteraction;
  var _useScreenRenderConte = useScreenRenderContext(),
    isView = _useScreenRenderConte.isView,
    isPreview = _useScreenRenderConte.isPreview,
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isEdit = _useScreenRenderConte.isEdit;
  var hasHeader = isHeaderFilled(header);
  var hasFooter = isFooterFilled(footer);
  var footerProps = getFooterProps(footer, {
    isView: isView,
    current: current,
    openWebView: openWebView,
    isPreview: isPreview
  });
  var hasTitle = isTextFilled(title);
  var _ref2 = title || {},
    _ref2$textStyle = _ref2.textStyle,
    titleTextStyle = _ref2$textStyle === void 0 ? null : _ref2$textStyle;
  var hasSubtitle = isTextFilled(subtitle);
  var _useDimensionObserver = useDimensionObserver(),
    headerRef = _useDimensionObserver.ref,
    _useDimensionObserver2 = _useDimensionObserver.height,
    headerHeight = _useDimensionObserver2 === void 0 ? 0 : _useDimensionObserver2;
  var _useDimensionObserver3 = useDimensionObserver(),
    footerRef = _useDimensionObserver3.ref,
    _useDimensionObserver4 = _useDimensionObserver3.height,
    footerHeight = _useDimensionObserver4 === void 0 ? 0 : _useDimensionObserver4;
  var backgroundPlaying = current && (isView || isEdit);
  var mediaShouldLoad = !isPlaceholder && (current || preload);
  var isInteractivePreview = isEdit && screenState === null;
  var _ref3 = keypadSettings || {},
    _ref3$layout = _ref3.layout,
    keypadLayout = _ref3$layout === void 0 ? null : _ref3$layout;
  var _ref4 = keypadLayout || {},
    _ref4$columnAlign = _ref4.columnAlign,
    columnAlign = _ref4$columnAlign === void 0 ? null : _ref4$columnAlign,
    _ref4$columns = _ref4.columns,
    columns = _ref4$columns === void 0 ? null : _ref4$columns,
    _ref4$spacing = _ref4.spacing,
    columnSpacing = _ref4$spacing === void 0 ? null : _ref4$spacing,
    _ref4$withSquareItems = _ref4.withSquareItems,
    withSquareItems = _ref4$withSquareItems === void 0 ? false : _ref4$withSquareItems;
  var _ref5 = buttonStyles || {},
    _ref5$layout = _ref5.layout,
    buttonLayout = _ref5$layout === void 0 ? null : _ref5$layout,
    _ref5$textStyle = _ref5.textStyle,
    buttonTextStyle = _ref5$textStyle === void 0 ? null : _ref5$textStyle,
    _ref5$boxStyle = _ref5.boxStyle,
    buttonBoxStyle = _ref5$boxStyle === void 0 ? null : _ref5$boxStyle,
    _ref5$fillImage = _ref5.fillImage,
    fillImage = _ref5$fillImage === void 0 ? false : _ref5$fillImage,
    _ref5$alignment = _ref5.alignment,
    buttonAlignment = _ref5$alignment === void 0 ? null : _ref5$alignment;
  var _ref6 = popupStyles || {},
    _ref6$layout = _ref6.layout,
    popupLayout = _ref6$layout === void 0 ? null : _ref6$layout,
    _ref6$headingTextStyl = _ref6.headingTextStyle,
    headingTextStyle = _ref6$headingTextStyl === void 0 ? null : _ref6$headingTextStyl,
    _ref6$contentTextStyl = _ref6.contentTextStyle,
    contentTextStyle = _ref6$contentTextStyl === void 0 ? null : _ref6$contentTextStyl,
    _ref6$button = _ref6.button,
    popupButtons = _ref6$button === void 0 ? null : _ref6$button,
    _ref6$boxStyle = _ref6.boxStyle,
    popupBoxStyle = _ref6$boxStyle === void 0 ? null : _ref6$boxStyle;
  var _ref7 = popupButtons || {},
    _ref7$buttonTextStyle = _ref7.buttonTextStyle,
    popupButtonsTextStyle = _ref7$buttonTextStyle === void 0 ? null : _ref7$buttonTextStyle,
    _ref7$boxStyle = _ref7.boxStyle,
    popupButtonsBoxStyle = _ref7$boxStyle === void 0 ? null : _ref7$boxStyle;
  var popupLayoutClassName = useMemo(function () {
    return popupLayout !== null ? camelCase(popupLayout) : '';
  }, [popupLayout]);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showPopup = _useState2[0],
    setShowPopup = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    popup = _useState4[0],
    setPopup = _useState4[1];
  var _ref8 = popup || {},
    _ref8$heading = _ref8.heading,
    popupHeading = _ref8$heading === void 0 ? null : _ref8$heading,
    _ref8$content = _ref8.content,
    popupContent = _ref8$content === void 0 ? null : _ref8$content,
    _ref8$largeVisual = _ref8.largeVisual,
    largeVisual = _ref8$largeVisual === void 0 ? null : _ref8$largeVisual,
    _ref8$button = _ref8.button,
    popupButton = _ref8$button === void 0 ? null : _ref8$button,
    _ref8$popupBoxStyle = _ref8.popupBoxStyle,
    singlePopupBoxStyle = _ref8$popupBoxStyle === void 0 ? null : _ref8$popupBoxStyle;
  var hasPopupHeading = isTextFilled(popupHeading);
  var _ref9 = popupHeading || {},
    _ref9$textStyle = _ref9.textStyle,
    popupHeadingTextStyle = _ref9$textStyle === void 0 ? null : _ref9$textStyle;
  var hasPopupContent = isTextFilled(popupContent);
  var _ref0 = popupContent || {},
    _ref0$textStyle = _ref0.textStyle,
    popupContentTextStyle = _ref0$textStyle === void 0 ? null : _ref0$textStyle;
  var _ref1 = popupButton || {},
    _ref1$label = _ref1.label,
    buttonLabel = _ref1$label === void 0 ? null : _ref1$label,
    _ref1$url = _ref1.url,
    buttonUrl = _ref1$url === void 0 ? null : _ref1$url,
    _ref1$inWebView = _ref1.inWebView,
    popupInWebView = _ref1$inWebView === void 0 ? false : _ref1$inWebView,
    _ref1$boxStyle = _ref1.boxStyle,
    popupButtonBoxStyle = _ref1$boxStyle === void 0 ? null : _ref1$boxStyle;
  var onItemClick = useCallback(function (e, item, index) {
    e.stopPropagation();
    var _ref10 = item || {},
      _ref10$label = _ref10.label,
      itemLabel = _ref10$label === void 0 ? null : _ref10$label,
      _ref10$heading = _ref10.heading,
      heading = _ref10$heading === void 0 ? null : _ref10$heading,
      _ref10$inWebView = _ref10.inWebView,
      inWebView = _ref10$inWebView === void 0 ? false : _ref10$inWebView,
      _ref10$url = _ref10.url,
      url = _ref10$url === void 0 ? null : _ref10$url;
    if (inWebView && url !== null) {
      openWebView({
        url: url
      });
    } else {
      setPopup(item);
      setShowPopup(true);
    }
    var _ref11 = heading || {},
      _ref11$body = _ref11.body,
      headingBody = _ref11$body === void 0 ? null : _ref11$body;
    var finalLabel = isString(itemLabel) ? itemLabel : (itemLabel || {}).body || null;
    trackScreenEvent('click_item', ["#".concat(index + 1), finalLabel || headingBody || ''].filter(function (it) {
      return !isEmpty(it);
    }).join(' '), {
      linkType: 'keypad_item',
      linkUrl: url || null
    });
  }, [setPopup, setShowPopup, trackScreenEvent, openWebView]);
  var onCloseModal = useCallback(function () {
    setShowPopup(false);
    trackScreenEvent('close_modal');
  }, [setShowPopup, trackScreenEvent]);
  var onClickClose = useCallback(function (e) {
    e.preventDefault();
    e.stopPropagation();
    onCloseModal();
  }, [onCloseModal]);
  var onClickCta = useCallback(function () {
    var e = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    if (e !== null) {
      e.stopPropagation();
    }
  }, []);
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    popupDragDirection = _useState6[0],
    setPopupDragDirection = _useState6[1];
  var onPopupScrollHeightChange = useCallback(function (_ref12) {
    var _ref12$scrolleeHeight = _ref12.scrolleeHeight,
      scrolleeHeight = _ref12$scrolleeHeight === void 0 ? 0 : _ref12$scrolleeHeight;
    if (Math.floor(scrolleeHeight) >= Math.floor(height)) {
      setPopupDragDirection('top');
    } else {
      setPopupDragDirection('bottom');
    }
  }, [height]);
  var computePopupProgress = useCallback(function (_ref13) {
    var dragActive = _ref13.active,
      _ref13$movement = _slicedToArray(_ref13.movement, 2),
      my = _ref13$movement[1],
      _ref13$velocity = _slicedToArray(_ref13.velocity, 2),
      vy = _ref13$velocity[1];
    var damper = 0.5;
    var delta = Math.abs(my) / window.innerHeight;
    var reachedThreshold = vy > 1 || delta > 0.3;
    var progress = 0;
    if (popupDragDirection === 'top' && my < 0) {
      progress = delta * damper * -1;
    } else if (popupDragDirection === 'bottom' && my > 0) {
      progress = delta * damper;
    }
    if (!dragActive) {
      if (reachedThreshold) {
        onCloseModal();
        return 1;
      }
      return 0;
    }
    return progress;
  }, [onCloseModal, popupDragDirection]);
  useEffect(function () {
    if (showPopup) {
      disableInteraction();
    } else {
      enableInteraction();
    }
  }, [showPopup]);
  useEffect(function () {
    function handleClickOutside(e) {
      if (!current && isView || popupInnerRef.current && !popupInnerRef.current.contains(e.target) && containerRef.current && containerRef.current.contains(e.target) && !isInteractivePreview && !isEdit && showPopup) {
        e.preventDefault();
        e.stopPropagation();
        onCloseModal();
      }
    }
    document.addEventListener('mouseup', handleClickOutside);
    return function () {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [current, popupInnerRef, containerRef, isInteractivePreview, isEdit, showPopup]);
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    popupDragDisabled = _useState8[0],
    setPopupDragDisabled = _useState8[1];
  var onPopupScrollBottom = useCallback(function () {
    setPopupDragDisabled(false);
  }, [setPopupDragDisabled]);
  var onPopupScrollNotBottom = useCallback(function () {
    setPopupDragDisabled(true);
  }, [setPopupDragDisabled]);
  var onTap = useCallback(function () {
    onCloseModal();
  }, [onCloseModal]);
  var _useDragProgress = useDragProgress({
      disabled: !isView || popupDragDisabled,
      progress: showPopup ? 0 : 1,
      computeProgress: computePopupProgress,
      springParams: {
        config: {
          tension: 300,
          friction: 30
        }
      },
      dragOptions: {
        filterTaps: true,
        preventDefault: true,
        stopPropagation: true
      },
      onTap: onTap
    }),
    bindPopupDrag = _useDragProgress.bind,
    popupSpring = _useDragProgress.progress;
  useEffect(function () {
    var keyup = function keyup(e) {
      if (e.key === 'Escape') {
        if (showPopup) {
          onCloseModal();
        }
      }
    };
    document.addEventListener('keyup', keyup);
    return function () {
      document.removeEventListener('keyup', keyup);
    };
  }, [showPopup, onCloseModal]);
  var gridItems = useMemo(function () {
    return (items === null || items.length === 0 ? placeholders : items).map(function (item, index) {
      var _ref14 = item || {},
        _ref14$id = _ref14.id,
        id = _ref14$id === void 0 ? null : _ref14$id,
        _ref14$label = _ref14.label,
        itemLabel = _ref14$label === void 0 ? null : _ref14$label,
        _ref14$visual = _ref14.visual,
        visual = _ref14$visual === void 0 ? null : _ref14$visual,
        _ref14$boxStyle = _ref14.boxStyle,
        boxStyle = _ref14$boxStyle === void 0 ? null : _ref14$boxStyle,
        _ref14$alignment = _ref14.alignment,
        alignment = _ref14$alignment === void 0 ? null : _ref14$alignment,
        _ref14$heading = _ref14.heading,
        heading = _ref14$heading === void 0 ? null : _ref14$heading,
        _ref14$content = _ref14.content,
        content = _ref14$content === void 0 ? null : _ref14$content,
        _ref14$url = _ref14.url,
        url = _ref14$url === void 0 ? null : _ref14$url,
        _ref14$inWebView = _ref14.inWebView,
        inWebView = _ref14$inWebView === void 0 ? false : _ref14$inWebView,
        _ref14$largeVisual = _ref14.largeVisual,
        popupLargeVisual = _ref14$largeVisual === void 0 ? null : _ref14$largeVisual;
      var _ref15 = visual || {},
        _ref15$url = _ref15.url,
        visualUrl = _ref15$url === void 0 ? null : _ref15$url;
      var _ref16 = heading || {},
        _ref16$body = _ref16.body,
        headingBody = _ref16$body === void 0 ? null : _ref16$body;
      var _ref17 = content || {},
        _ref17$body = _ref17.body,
        contentBody = _ref17$body === void 0 ? null : _ref17$body;
      var finalLabel = isString(itemLabel) ? {
        body: itemLabel
      } : itemLabel || {};
      var _ref18 = finalLabel || {},
        _ref18$body = _ref18.body,
        label = _ref18$body === void 0 ? null : _ref18$body,
        _ref18$textStyle = _ref18.textStyle,
        finalLabelTextStyle = _ref18$textStyle === void 0 ? null : _ref18$textStyle;
      var key = label || visualUrl || id;
      var itemIsEmpty = label === null && visual === null;
      var isExternalLink = url !== null && !inWebView;
      var isPopupEmpty = (heading === null || headingBody === null || headingBody === '') && (content === null || contentBody === null || contentBody === '') && popupLargeVisual === null;
      var finalTextStyle = _objectSpread(_objectSpread({}, buttonTextStyle), finalLabelTextStyle);
      return /*#__PURE__*/React.createElement("div", {
        key: key,
        className: styles.item
      }, /*#__PURE__*/React.createElement(Button, {
        className: classNames([styles.button, _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, styles.layoutLabelBottom, buttonLayout === 'label-bottom'), styles.layoutLabelTop, buttonLayout === 'label-top'), styles.layoutNoLabel, buttonLayout === 'no-label'), styles.layoutLabelOver, buttonLayout === 'label-over'), styles.fillImage, fillImage === true), styles.isEmpty, itemIsEmpty), styles.isLink, url !== null), styles.disableHover, isPopupEmpty && url === null)]),
        style: _objectSpread(_objectSpread(_objectSpread({}, getStyleFromBox(buttonBoxStyle)), getStyleFromBox(boxStyle)), getStyleFromAlignment(alignment || buttonAlignment, true, 'flex-start')),
        external: isExternalLink,
        href: isExternalLink ? url : null,
        focusable: current,
        onClick: !isPopupEmpty || url !== null && !isExternalLink ? function (e) {
          return onItemClick(e, item, index);
        } : null
      }, /*#__PURE__*/React.createElement(ScreenElement, {
        emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
          id: "Qanvis",
          defaultMessage: [{
            "type": 0,
            "value": "Visual"
          }]
        }),
        emptyClassName: classNames([styles.empty, styles.buttonVisual, styles.emptyButtonVisual]),
        isEmpty: visual === null
      }, visual !== null ? /*#__PURE__*/React.createElement(Visual, {
        className: styles.buttonVisual,
        imageClassName: styles.thumbnail,
        media: visual,
        width: "auto"
      }) : null), /*#__PURE__*/React.createElement(ScreenElement, {
        placeholder: /*#__PURE__*/React.createElement(PlaceholderButton, null),
        emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
          id: "Zbu5Uk",
          defaultMessage: [{
            "type": 0,
            "value": "Label"
          }]
        }),
        emptyClassName: classNames([styles.empty, styles.emptyButtonLabel]),
        isEmpty: label === null
      }, label !== null || !isInteractivePreview ? /*#__PURE__*/React.createElement(Text, Object.assign({
        className: styles.buttonLabel
      }, finalLabel, {
        textStyle: finalTextStyle
      })) : null)));
    });
  }, [items, screenState, keypadSettings, buttonAlignment, buttonBoxStyle, buttonTextStyle, buttonLayout]);
  useEffect(function () {
    if (screenState === 'popup' && isPlaceholder) {
      setPopup(placeholderPopupBoxStyles); // @note force placeholder
      setShowPopup(true);
    }
    if (screenState === 'keypad') {
      setPopup(null);
      setShowPopup(false);
    }
    if (screenState !== null && screenState.includes('popup')) {
      var index = screenState.split('.').pop();
      var found = items[index];
      setPopup(found);
      setShowPopup(true);
    }
    if (screenState === null && !isView) {
      setPopup(null);
      setShowPopup(false);
    }
  }, [screenState, items, isView, showPopup, setPopup, setShowPopup]);
  return /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    className: classNames([styles.container, _defineProperty(_defineProperty(_defineProperty({}, className, className !== null), styles.isPlaceholder, isPlaceholder), styles.withSquareItems, withSquareItems)]),
    "data-screen-ready": true
  }, isEdit && screenState !== null ? /*#__PURE__*/React.createElement("div", mouseBlocker) : null, !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    background: background,
    width: width,
    height: height,
    resolution: resolution,
    playing: backgroundPlaying,
    muted: muted,
    shouldLoad: mediaShouldLoad,
    mediaRef: mediaRef,
    className: styles.background
  }) : null, isView && !isPlaceholder && !withoutCloseButton ? /*#__PURE__*/React.createElement(animated.div, {
    className: classNames([styles.fixedHeader, _defineProperty({}, styles.open, showPopup)])
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.buttons
  }, /*#__PURE__*/React.createElement(Button, {
    className: styles.close,
    onClick: onClickClose,
    focusable: !isPreview && !isPlaceholder && showPopup,
    withoutStyle: true
  }, /*#__PURE__*/React.createElement(Close, {
    color: "#000",
    className: styles.closeIcon
  })))) : null, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.inner
  }, /*#__PURE__*/React.createElement(Scroll, {
    width: width,
    height: height,
    verticalAlign: layout,
    withArrow: !showPopup,
    withShadow: !showPopup,
    disabled: isPreview || isPlaceholder || showPopup
  }, /*#__PURE__*/React.createElement(Layout, {
    className: styles.layout,
    verticalAlign: layout,
    width: width,
    style: !isPlaceholder ? {
      padding: spacing,
      paddingTop: (hasHeader ? headerHeight : spacing) + (current && !isPreview ? viewerTopHeight : 0),
      paddingBottom: (hasFooter ? footerHeight : spacing) + (current && !isPreview ? viewerBottomHeight : 0)
    } : null
  }, !isPlaceholder && hasHeader ? /*#__PURE__*/React.createElement("div", {
    ref: headerRef,
    className: styles.header,
    style: {
      paddingTop: spacing / 2,
      paddingLeft: spacing,
      paddingRight: spacing,
      paddingBottom: spacing,
      transform: !isPreview ? "translate(0, ".concat(viewerTopHeight, "px)") : null
    }
  }, /*#__PURE__*/React.createElement(Header, header)) : null, /*#__PURE__*/React.createElement(ScreenElement, {
    placeholder: "Title",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "BSTWf8",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    }),
    emptyClassName: classNames([styles.empty, styles.emptyHeading]),
    isEmpty: !hasTitle
  }, hasTitle ? /*#__PURE__*/React.createElement(Heading, Object.assign({
    className: styles.title
  }, title, {
    textStyle: titleTextStyle
  })) : null), /*#__PURE__*/React.createElement(ScreenElement, {
    placeholder: "Subtitle",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "ZG8/oK",
      defaultMessage: [{
        "type": 0,
        "value": "Subtitle"
      }]
    }),
    emptyClassName: classNames([styles.empty, styles.emptyHeading]),
    isEmpty: !hasSubtitle
  }, hasSubtitle ? /*#__PURE__*/React.createElement(Text, Object.assign({
    className: styles.subtitle
  }, subtitle)) : null), /*#__PURE__*/React.createElement(Keypad, {
    className: classNames([styles.grid, _defineProperty({}, styles.gridPlaceholder, isPlaceholder)]),
    align: columnAlign,
    columns: isPlaceholder ? 3 : columns,
    spacing: isPlaceholder ? 2 : columnSpacing,
    items: gridItems
  }), !isPlaceholder && hasFooter ? /*#__PURE__*/React.createElement("div", {
    ref: footerRef,
    className: styles.footer,
    style: {
      transform: current && !isPreview ? "translate(0, -".concat(viewerBottomHeight, "px)") : null,
      paddingLeft: Math.max(spacing / 2, viewerBottomSidesWidth),
      paddingRight: Math.max(spacing / 2, viewerBottomSidesWidth),
      paddingBottom: spacing / 2,
      paddingTop: spacing
    }
  }, /*#__PURE__*/React.createElement(Footer, footerProps)) : null), popup !== null ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(animated.div, {
    className: classNames([styles.popupBackdrop]),
    style: {
      opacity: popupSpring.to(function (p) {
        return 1 - Math.abs(p);
      })
    }
  }), /*#__PURE__*/React.createElement(animated.div, Object.assign({
    className: styles.popup,
    style: {
      transform: popupSpring.to(function (p) {
        return "translateY(".concat(100 * p, "%) scale(").concat(1 - Math.abs(p * 0.5), ")");
      }),
      pointerEvents: popupSpring.to(function (p) {
        return Math.abs(p) > 0.5 ? 'none' : 'auto';
      })
    }
  }, bindPopupDrag()), /*#__PURE__*/React.createElement(Scroll, {
    disabled: isPreview || isPlaceholder,
    verticalAlign: "middle",
    withArrow: false,
    scrollPosition: !showPopup ? 1 : null,
    onScrolledBottom: onPopupScrollBottom,
    onScrolledNotBottom: onPopupScrollNotBottom,
    onScrollHeightChange: onPopupScrollHeightChange,
    className: styles.popupScroll,
    withShadow: true
  }, /*#__PURE__*/React.createElement("div", {
    ref: popupInnerRef,
    className: classNames([styles.popupInner, styles[popupLayoutClassName], _defineProperty({}, styles.withShadow, popupBoxStyle === null)]),
    style: _objectSpread(_objectSpread(_objectSpread({}, getStyleFromBox(placeholderPopupBoxStyles)), getStyleFromBox(popupBoxStyle)), getStyleFromBox(singlePopupBoxStyle))
  }, /*#__PURE__*/React.createElement(ScreenElement, {
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "IE7U/Q",
      defaultMessage: [{
        "type": 0,
        "value": "Heading"
      }]
    }),
    emptyClassName: classNames([styles.empty, styles.emptyHeading]),
    isEmpty: !hasPopupHeading
  }, hasPopupHeading ? /*#__PURE__*/React.createElement(Heading, Object.assign({
    className: styles.popupHeading
  }, popupHeading, {
    textStyle: _objectSpread(_objectSpread({}, headingTextStyle), popupHeadingTextStyle)
  })) : null), /*#__PURE__*/React.createElement(ScreenElement, {
    placeholder: "popupContent",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "JRZoe6",
      defaultMessage: [{
        "type": 0,
        "value": "Content"
      }]
    }),
    emptyClassName: classNames([styles.empty, styles.emptyContent]),
    isEmpty: !hasPopupContent
  }, hasPopupContent ? /*#__PURE__*/React.createElement(Text, Object.assign({
    className: styles.popupContent
  }, popupContent, {
    textStyle: _objectSpread(_objectSpread({}, contentTextStyle), popupContentTextStyle)
  })) : null), /*#__PURE__*/React.createElement(ScreenElement, {
    placeholder: "image",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "z4Pr+g",
      defaultMessage: [{
        "type": 0,
        "value": "Visual (Image or Video)"
      }]
    }),
    emptyClassName: classNames([styles.empty, styles.emptyVisual]),
    isEmpty: largeVisual === null
  }, largeVisual !== null ? /*#__PURE__*/React.createElement(Visual, {
    className: styles.popupVisual,
    media: largeVisual,
    width: "100%"
  }) : null), /*#__PURE__*/React.createElement(ScreenElement, {
    placeholder: "button",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "N7Kj8z",
      defaultMessage: [{
        "type": 0,
        "value": "Button"
      }]
    }),
    emptyClassName: classNames([styles.empty, styles.emptyCTA]),
    isEmpty: popupButton === null
  }, popupButton !== null ? /*#__PURE__*/React.createElement(CallToAction, {
    className: styles.popupCTA,
    label: _objectSpread(_objectSpread({}, popupButtonsTextStyle), buttonLabel),
    url: buttonUrl,
    onClick: onClickCta,
    inWebView: popupInWebView,
    openWebView: openWebView,
    type: "click",
    boxStyle: _objectSpread(_objectSpread({}, popupButtonsBoxStyle), popupButtonBoxStyle)
  }) : null))))) : null)));
};
KeypadScreen.propTypes = propTypes;
KeypadScreen.defaultProps = defaultProps;

var definition = [{
  id: 'keypad',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "cGItoy",
      defaultMessage: [{
        "type": 0,
        "value": "List"
      }]
    }),
    order: 4
  },
  title: defineMessage({
    id: "75nZbY",
    defaultMessage: [{
      "type": 0,
      "value": "Keypad"
    }]
  }),
  layouts: ['top', 'middle', 'bottom'],
  component: KeypadScreen,
  states: [{
    id: 'keypad',
    label: defineMessage({
      id: "Z4f5qh",
      defaultMessage: [{
        "type": 0,
        "value": "Buttons"
      }]
    }),
    fields: [{
      name: 'layout',
      type: 'screen-layout',
      defaultValue: 'middle',
      label: defineMessage({
        id: "4iBXj2",
        defaultMessage: [{
          "type": 0,
          "value": "Layout"
        }]
      })
    }, {
      name: 'keypadSettings',
      type: 'fields',
      isList: false,
      withoutLabel: true,
      defaultValue: {
        layout: {
          columnAlign: 'middle',
          columns: 3,
          spacing: 2,
          withSquareItems: false
        }
      },
      label: defineMessage({
        id: "FjuRaw",
        defaultMessage: [{
          "type": 0,
          "value": "Keypad settings"
        }]
      }),
      fields: [{
        name: 'layout',
        type: 'keypad-layout',
        isList: true
      }]
    }, {
      name: 'items',
      type: 'buttons',
      label: defineMessage({
        id: "fbUW2U",
        defaultMessage: [{
          "type": 0,
          "value": "Items"
        }]
      }),
      isSection: true
    }, {
      name: 'buttonStyles',
      type: 'fields',
      defaultValue: {
        layout: 'label-bottom',
        boxStyle: {
          backgroundColor: {
            alpha: 0.15,
            color: '#000000'
          }
        }
      },
      isList: true,
      fields: [{
        name: 'layout',
        type: 'button-layout',
        label: defineMessage({
          id: "OPxJx/",
          defaultMessage: [{
            "type": 0,
            "value": "Layout"
          }]
        })
      }, {
        name: 'textStyle',
        type: 'text-style-form',
        label: defineMessage({
          id: "rzXCu2",
          defaultMessage: [{
            "type": 0,
            "value": "Label style"
          }]
        })
      }, {
        name: 'boxStyle',
        type: 'box-style-form',
        label: defineMessage({
          id: "nno1Pq",
          defaultMessage: [{
            "type": 0,
            "value": "Button style"
          }]
        })
      }, {
        name: 'fillImage',
        type: 'toggle',
        label: defineMessage({
          id: "6LFjCH",
          defaultMessage: [{
            "type": 0,
            "value": "Fill with image"
          }]
        })
      }
      // This works but might not be the best way
      // {
      //     name: 'alignment',
      //     type: 'alignment',
      //     label: defineMessage({
      //         defaultMessage: 'Button alignment',
      //         description: 'Field label',
      //     }),
      // },
      ]
    }]
  }, {
    id: 'popups',
    label: defineMessage({
      id: "FYm4w+",
      defaultMessage: [{
        "type": 0,
        "value": "Popups"
      }]
    }),
    fields: [{
      name: 'popupStyles',
      type: 'fields',
      defaultValue: {
        layout: 'content-top'
      },
      fields: [{
        name: 'layout',
        type: 'card-layout',
        label: defineMessage({
          id: "OPxJx/",
          defaultMessage: [{
            "type": 0,
            "value": "Layout"
          }]
        })
      }, {
        name: 'headingTextStyle',
        type: 'text-style-form',
        label: defineMessage({
          id: "zrK6ak",
          defaultMessage: [{
            "type": 0,
            "value": "Heading text style"
          }]
        })
      }, {
        name: 'contentTextStyle',
        type: 'text-style-form',
        label: defineMessage({
          id: "z9nSIG",
          defaultMessage: [{
            "type": 0,
            "value": "Content text style"
          }]
        })
      }, {
        name: 'boxStyle',
        type: 'box-style-form',
        label: defineMessage({
          id: "p1pghO",
          defaultMessage: [{
            "type": 0,
            "value": "Box style"
          }]
        })
      }, {
        name: 'button',
        type: 'fields',
        isList: true,
        label: defineMessage({
          id: "i6bmbD",
          defaultMessage: [{
            "type": 0,
            "value": "Button"
          }]
        }),
        fields: [{
          name: 'buttonTextStyle',
          type: 'text-style-form',
          label: defineMessage({
            id: "p5RHzq",
            defaultMessage: [{
              "type": 0,
              "value": "Button text style"
            }]
          })
        }, {
          name: 'buttonStyle',
          type: 'box-style-form',
          label: defineMessage({
            id: "nno1Pq",
            defaultMessage: [{
              "type": 0,
              "value": "Button style"
            }]
          })
        }]
      }]
    }]
  }, {
    id: 'popup',
    label: defineMessage({
      id: "sSahY1",
      defaultMessage: [{
        "type": 0,
        "value": "Popup"
      }]
    }),
    defaultValue: [],
    repeatable: true,
    fieldName: 'items',
    fields: [{
      name: 'heading',
      type: 'heading-element',
      label: defineMessage({
        id: "6iR9Mt",
        defaultMessage: [{
          "type": 0,
          "value": "Heading"
        }]
      })
    }, {
      name: 'content',
      type: 'text-element',
      label: defineMessage({
        id: "uoF5Pv",
        defaultMessage: [{
          "type": 0,
          "value": "Content"
        }]
      }),
      theme: {
        label: {
          textStyle: 'text'
        }
      }
    }, {
      name: 'largeVisual',
      type: 'visual',
      label: defineMessage({
        id: "HwqzVR",
        defaultMessage: [{
          "type": 0,
          "value": "Visual"
        }]
      })
    }, {
      name: 'popupBoxStyle',
      type: 'box-style-form',
      label: defineMessage({
        id: "p1pghO",
        defaultMessage: [{
          "type": 0,
          "value": "Box style"
        }]
      })
    }, {
      name: 'button',
      type: 'button-link',
      label: defineMessage({
        id: "i6bmbD",
        defaultMessage: [{
          "type": 0,
          "value": "Button"
        }]
      }),
      theme: {
        label: {
          textStyle: 'button'
        }
      }
    }]
  }],
  fields: [{
    name: 'title',
    type: 'heading-element',
    theme: {
      textStyle: 'heading1'
    },
    label: defineMessage({
      id: "N25iDO",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    })
  }, {
    name: 'subtitle',
    type: 'text-element',
    theme: {
      textStyle: 'text'
    },
    label: defineMessage({
      id: "EcZYpr",
      defaultMessage: [{
        "type": 0,
        "value": "Subtitle"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}];

export { KeypadScreen as Keypad, definition as default };
